import './list-vehicle.scss';

import { LocalRole } from '@core/model/local-role.enum';
import { useUserIdentityContext } from '@core/services/user-identity.context';
import { GetVehicleModel } from "@factory/model/query-model-vehicle";
import { useQyGetVehicle } from "@factory/query/vehicle.query";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from '@shared/ui/header-content/header-content';
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { Button } from "primereact/button";
import { Column } from 'primereact/column';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Paginator, PaginatorPageChangeEvent } from 'primereact/paginator';
import { Sidebar } from "primereact/sidebar";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";

import { FilterVehicle } from "../filter-vehicle/filter-vehicle";
import { useFilterVehicleContext } from "../filter-vehicle/filter-vehicle.context";

export function ListCustomerVehicle() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { getAuthUser, getUserRole } = useUserIdentityContext();
  const [rowLimit, setRowLimit] = useState(20);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);    
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });  
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, getFilterEntityValues } = useFilterVehicleContext();
  const { personId } = useParams()
  const userCode = personId || getAuthUser().userCode;
  const userRole = getUserRole().name;

  // THIS IS THE QUERY DECLARATION
  const {
    data: vehicleResponse,
    isLoading,
    isError,
    error,
  } = useQyGetVehicle(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    {
      ...(userRole === LocalRole.CardOwner ? {user_code: userCode} : (personId ? {user_code: personId} : {})),
    },
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetVehicleModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterVehicle />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{ vehicleResponse?.count }</b>
      </h5>

      <DataTable
        value={ vehicleResponse?.data }
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column field="plate_number" header="Plate Number" sortable></Column>
        <Column field="make" header="Make" sortable></Column>
        <Column field="model" header="Model" sortable></Column>
        <Column field="year" header="Year" sortable></Column>
        <Column field="color" header="Color" sortable></Column>
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={ vehicleResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListCustomerVehicle" className="vehicle">
      <HeaderContent title={<h1>Vehicle</h1>}>
        {userRole !== LocalRole.CardOwner && (
          <span className="flex gap-2">
            <Button label="New" onClick={handleNew} />
          </span>
        )}
      </HeaderContent>

       <div className="p-7">
        {filterElement}
        {isLoading && displayLoading}
        {isError && !isLoading && displayError}
        {!isLoading && !isError && grid}
      </div>
    </div>
  );
}

export default ListCustomerVehicle;
